import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import CoursesPage from './pages/CoursesPage';
import CoursePage from './pages/CoursePage';
import LessonPage from './pages/LessonPage';
import BookmarksPage from './pages/BookmarksPage';
import PodiaMovedPage from './pages/PodiaMovedPage';
import { ALAKZAT, GYERTYA } from './config/felismero';

// A gyakorló a grafikonkönyvtárral (recharts) együtt külön darabban töltődik.
const GyakorloPage = lazy(() => import('./pages/GyakorloPage'));
const OlvasoPage = lazy(() => import('./pages/OlvasoPage'));
const AlakzatPage = lazy(() => import('./pages/AlakzatPage'));
const AlakzatDemoPage = lazy(() => import('./pages/AlakzatPage').then(m => ({ default: m.AlakzatDemoPage })));
const GyakorloDemoPage = lazy(() => import('./pages/GyakorloPage').then(m => ({ default: m.GyakorloDemoPage })));
const pageFallback = <div style={{ minHeight: '100vh', background: '#0d1117' }} />;

// Podia storefront paths (the egyetem domain used to be the Podia site).
// Each one, with or without a slug, shows the "courses moved" page instead
// of falling through to the catch-all and an unexplained /login.
const PODIA_PATHS = [
  'courses', 'products', 'bundles', 'coaching', 'downloads', 'digital-downloads',
  'webinars', 'communities', 'community', 'posts', 'library', 'checkout', 'cart',
];

function App() {
  const { session, access, loading, profile, refreshAccess } = useAuth();

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route
          path="/tanfolyamok"
          element={
            <ProtectedRoute session={session} loading={loading}>
              <CoursesPage session={session} access={access} profile={profile} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tanfolyam/:slug"
          element={
            <ProtectedRoute session={session} loading={loading}>
              <CoursePage session={session} access={access} profile={profile} onAccessChange={refreshAccess} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lecke/:lessonId"
          element={
            <ProtectedRoute session={session} loading={loading}>
              <LessonPage session={session} access={access} profile={profile} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/bookmarks"
          element={
            <ProtectedRoute session={session} loading={loading}>
              <BookmarksPage session={session} access={access} />
            </ProtectedRoute>
          }
        />

        {/* Kijelentkezve a /gyakorlo a nyilvános bemutató (egy helyzet, regisztráció nélkül),
            bejelentkezve a lista. A helyzetek és a záró kártya bejelentkezéshez kötöttek. */}
        <Route
          path="/gyakorlo"
          element={loading ? pageFallback : (
            <Suspense fallback={pageFallback}>
              {session ? <GyakorloPage session={session} access={access} /> : <GyakorloDemoPage />}
            </Suspense>
          )}
        />
        {/* Alakzatfelismerő és Gyertyaminta-felismerő (egy motor, két készlet): kijelentkezve a nyilvános
            bemutató (5 feladat, mentés nélkül), bejelentkezve az áttekintés és a körök. A feladatok és a
            megoldás a szerverről jönnek. */}
        {[ALAKZAT, GYERTYA].map(k => (
          <Route
            key={k.path}
            path={k.path}
            element={loading ? pageFallback : (
              <Suspense fallback={pageFallback}>
                {session ? <AlakzatPage key={k.keszlet} keszlet={k} session={session} access={access} /> : <AlakzatDemoPage key={k.keszlet} keszlet={k} />}
              </Suspense>
            )}
          />
        ))}
        {/* Segédletek az appban: az olvasó. Kijelentkezve is megnyílik (2. kör): a szabad anyag olvasható,
            a többinél belépés / regisztráció; a jogosultságot a szerver dönti el. */}
        <Route
          path="/olvaso/:slug/*"
          element={loading ? pageFallback : (
            <Suspense fallback={pageFallback}>
              <OlvasoPage session={session} />
            </Suspense>
          )}
        />
        <Route
          path="/gyakorlo/:id"
          element={
            <ProtectedRoute session={session} loading={loading}>
              <Suspense fallback={pageFallback}>
                <GyakorloPage session={session} access={access} />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {PODIA_PATHS.flatMap(p => [
          <Route key={p} path={`/${p}`} element={<PodiaMovedPage session={session} />} />,
          <Route key={`${p}/*`} path={`/${p}/*`} element={<PodiaMovedPage session={session} />} />,
        ])}

        <Route path="/" element={<Navigate to="/tanfolyamok" replace />} />
        <Route path="*" element={<Navigate to="/tanfolyamok" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
